import React, { useRef, useEffect, useState } from "react";

/**
 * Hybrid flavor image: auto-detects aspect ratio and sets data-aspect for CSS
 * Also handles loading states and image placeholders
 */
export default function FlavorImage({ src, alt }) {
  const imgRef = useRef(null);
  const [aspect, setAspect] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  // Get CDN URL at render time so it reflects loader.js config in embedded contexts
  const cdnUrl = (() => {
    if (typeof window === 'undefined') {
      return 'https://ddeewr0txvw1f.cloudfront.net';
    }
    if (window.STORE_LOCATOR_CONFIG?.CDN_URL) {
      return window.STORE_LOCATOR_CONFIG.CDN_URL.replace(/\/$/, '');
    }
    if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      return window.location.origin.replace(/\/$/, '');
    }
    return 'https://ddeewr0txvw1f.cloudfront.net';
  })();

  // Default image to use as fallback
  const DEFAULT_IMAGE = `${cdnUrl}/products/good-vibes-500.jpg`;
  
  // Ensure src is absolute — relative paths break when embedded on external sites
  const processedSrc = src && !src.startsWith('http')
    ? `${cdnUrl}${src.startsWith('/') ? src : `/${src}`}`
    : src;
  
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    
    const img = imgRef.current;
    if (!img) return;
    
    function handleLoad() {
      setIsLoading(false);
      if (img.naturalWidth && img.naturalHeight) {
        const ratio = img.naturalWidth / img.naturalHeight;
        if (ratio < 0.8) setAspect("tall");
        else if (ratio > 1.2) setAspect("wide");
        else setAspect("");
      }
    }
    
    function handleError() {
      setIsLoading(false);
      setHasError(true);
    }
    
    if (img.complete) handleLoad();
    else {
      img.addEventListener("load", handleLoad);
      img.addEventListener("error", handleError);
    }
    
    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [processedSrc]);

  return (
    <div className={`product-flavor-image ${isLoading ? 'loading' : ''} ${hasError ? 'error' : ''}`}>
      <img
        ref={imgRef}
        src={processedSrc || DEFAULT_IMAGE}
        alt={alt}
        loading="lazy"
        decoding="async"
        data-aspect={aspect || undefined}
        onError={(e) => {
          if (processedSrc !== DEFAULT_IMAGE) {
            e.target.src = DEFAULT_IMAGE;
          }
        }}
      />
      {isLoading && <div className="image-loader">Loading...</div>}
    </div>
  );
}
